import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinned, MapPin, Loader2, Eye, EyeOff, Target, Check } from 'lucide-react';
import axios from '../lib/axios';
import { API } from '../App';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom draggable marker icon
const createMarkerIcon = () => {
  return L.divIcon({
    className: 'custom-location-marker',
    html: `
      <div style="
        width: 40px; 
        height: 40px; 
        background: linear-gradient(135deg, #3b82f6, #1d4ed8); 
        border-radius: 50% 50% 50% 0; 
        transform: rotate(-45deg); 
        border: 3px solid white; 
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg); 
          color: white; 
          font-size: 16px;
        ">📍</div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

// Component to handle map center changes
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, zoom || 15, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Draggable marker component
const DraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState(position);

  useEffect(() => {
    setMarkerPosition(position);
  }, [position]);

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const newPos = marker.getLatLng();
        setMarkerPosition([newPos.lat, newPos.lng]);
        onPositionChange(newPos.lat, newPos.lng);
      }
    },
  };

  if (!markerPosition || !markerPosition[0] || !markerPosition[1]) {
    return null;
  }

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={markerPosition}
      ref={markerRef}
      icon={createMarkerIcon()}
    />
  );
};

// Click handler to set location
const LocationClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const LocationPicker = ({ 
  location, 
  onLocationChange, 
  onDisplayTypeChange,
  onAddressChange, // New: callback for address changes from reverse geocoding
  onLocationFetchSuccess, // New: callback to notify parent that GPS fetch succeeded
  onLocationFetchFail, // New: callback to notify parent that GPS fetch failed
  displayType = 'exact',
  disabled = false,
  language = 'en' // Language prop for translations
}) => {
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  
  // Default center (Curaçao)
  const defaultCenter = [12.1696, -68.9900];
  const defaultZoom = 11;
  
  const hasLocation = location?.latitude && location?.longitude;
  const center = hasLocation ? [location.latitude, location.longitude] : defaultCenter;
  const zoom = hasLocation ? 16 : defaultZoom;

  // Reverse geocode coordinates to get address using backend API
  const reverseGeocode = async (lat, lng) => {
    setReverseGeocoding(true);
    try {
      const response = await axios.get(`${API}/geocode/reverse`, {
        params: { lat, lng }
      });
      
      if (response.data && !response.data.error) {
        const data = response.data;
        
        // Notify parent of address changes
        if (onAddressChange) {
          onAddressChange({
            address: data.address || '',
            city: data.city || '',
            area: data.area || '',
            country: data.country || '',
          });
        }
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    } finally {
      setReverseGeocoding(false);
    }
  };

  // Get user's current location
  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      if (onLocationFetchFail) onLocationFetchFail();
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onLocationChange(latitude, longitude);
        setShowMap(true);
        setGettingLocation(false);
        
        // Reverse geocode to get address
        reverseGeocode(latitude, longitude);
        
        // Notify parent of success
        if (onLocationFetchSuccess) onLocationFetchSuccess();
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Failed to get your location. Please select your area manually or click on the map.');
        setGettingLocation(false);
        setShowMap(true); // Still show map so user can click
        
        // Notify parent of failure
        if (onLocationFetchFail) onLocationFetchFail();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Handle marker drag or map click
  const handlePositionChange = (lat, lng) => {
    onLocationChange(lat, lng);
    // Also reverse geocode when user moves the pin
    reverseGeocode(lat, lng);
  };

  // Translations for buttons
  const translations = {
    getLocation: {
      en: 'Use My Location',
      nl: 'Gebruik Mijn Locatie',
      es: 'Usar Mi Ubicación'
    },
    chooseOnMap: {
      en: 'Select area on map',
      nl: 'Selecteer buurt op de kaart',
      es: 'Seleccionar zona en el mapa'
    },
    locationCaptured: {
      en: 'Location set',
      nl: 'Locatie ingesteld',
      es: 'Ubicación establecida'
    },
    fetchingAddress: {
      en: '(fetching address...)',
      nl: '(adres ophalen...)',
      es: '(obteniendo dirección...)'
    },
    showAreaOnly: {
      en: 'Show Neighborhood Only',
      nl: 'Toon Alleen Buurt',
      es: 'Mostrar Solo Zona'
    },
    showExactPin: {
      en: 'Show Exact Pin',
      nl: 'Toon Exacte Pin',
      es: 'Mostrar Pin Exacto'
    },
    buyersWillSee: {
      en: 'Buyers will see',
      nl: 'Kopers zien',
      es: 'Los compradores verán'
    },
    areaRadius: {
      en: '~500m area',
      nl: 'buurt ~500m',
      es: 'zona ~500m'
    },
    exactLocation: {
      en: 'exact location',
      nl: 'exacte locatie',
      es: 'ubicación exacta'
    }
  };

  return (
    <div className="space-y-5">
      {/* Get Location Buttons */}
      <div className="flex flex-wrap gap-3 mt-2">
        <button
          type="button"
          onClick={handleGetCurrentLocation}
          disabled={disabled || gettingLocation}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="get-my-location-btn"
        >
          {gettingLocation ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <MapPinned className="w-4 h-4" />
          )}
          {translations.getLocation[language] || translations.getLocation.en}
        </button>
        
        {/* Show "Choose on Map" only when no location and map not yet shown */}
        {!showMap && !hasLocation && (
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-foreground rounded-xl hover:bg-secondary/80 transition-colors"
            data-testid="choose-on-map-btn"
          >
            <Target className="w-4 h-4" />
            {translations.chooseOnMap[language] || translations.chooseOnMap.en}
          </button>
        )}
        
        {/* Subtle inline location confirmation */}
        {hasLocation && (
          <div className="flex items-center gap-1.5 px-3 py-2 text-sm text-green-600 dark:text-green-400">
            <Check className="w-4 h-4" />
            <span className="font-medium">
              {translations.locationCaptured[language] || translations.locationCaptured.en}
            </span>
            {reverseGeocoding && (
              <span className="text-xs text-muted-foreground ml-1">
                {translations.fetchingAddress[language] || translations.fetchingAddress.en}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Privacy Toggle - NOW ABOVE THE MAP for better flow */}
      {hasLocation && (
        <div className="bg-muted/30 rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-foreground">
              {language === 'nl' ? 'Privacy instelling' 
                : language === 'es' ? 'Configuración de privacidad'
                : 'Privacy setting'}
            </h4>
            {/* Live preview indicator */}
            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
              {translations.buyersWillSee[language] || translations.buyersWillSee.en}: {displayType === 'area' 
                ? (translations.areaRadius[language] || translations.areaRadius.en)
                : (translations.exactLocation[language] || translations.exactLocation.en)}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Area Only Option - Now first since it's the default */}
            <button
              type="button"
              onClick={() => onDisplayTypeChange('area')}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                displayType === 'area'
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${displayType === 'area' ? 'bg-primary text-white' : 'bg-secondary'}`}>
                  <Target className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-medium text-foreground text-sm">
                    {translations.showAreaOnly[language] || translations.showAreaOnly.en}
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {language === 'nl' ? 'Meer privacy' : language === 'es' ? 'Más privacidad' : 'More privacy'}
                  </p>
                </div>
              </div>
            </button>
            
            {/* Exact Location Option */}
            <button
              type="button"
              onClick={() => onDisplayTypeChange('exact')}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                displayType === 'exact'
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${displayType === 'exact' ? 'bg-primary text-white' : 'bg-secondary'}`}>
                  <Eye className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-medium text-foreground text-sm">
                    {translations.showExactPin[language] || translations.showExactPin.en}
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {language === 'nl' ? 'Exacte pin zichtbaar' : language === 'es' ? 'Pin exacto visible' : 'Exact pin visible'}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Map - Always show when location exists or user chose to see it */}
      {(showMap || hasLocation) && (
        <div className="space-y-3">
          <div className="relative">
            {/* Preview badge on map */}
            {hasLocation && (
              <div className="absolute top-3 left-3 z-[1000] bg-background/95 backdrop-blur-sm border border-border rounded-lg px-3 py-1.5 shadow-sm">
                <span className="text-xs font-medium text-foreground">
                  {displayType === 'area' ? '👁️ ' : '📍 '}
                  {translations.buyersWillSee[language] || translations.buyersWillSee.en}: {displayType === 'area' 
                    ? (translations.areaRadius[language] || translations.areaRadius.en)
                    : (translations.exactLocation[language] || translations.exactLocation.en)}
                </span>
              </div>
            )}
            <div 
              className="h-[300px] rounded-xl overflow-hidden border border-border shadow-sm"
              style={{ zIndex: 0 }}
            >
              <MapContainer
                center={center}
                zoom={zoom}
                style={{ height: '100%', width: '100%' }}
                whenReady={() => setMapReady(true)}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
                
                <MapController center={center} zoom={zoom} />
                
                {hasLocation && (
                  <>
                    <DraggableMarker 
                      position={[location.latitude, location.longitude]}
                      onPositionChange={handlePositionChange}
                    />
                    
                    {/* Always show circle - different styling based on display type */}
                    <Circle
                      center={[location.latitude, location.longitude]}
                      radius={500} // 500 meters radius
                      pathOptions={displayType === 'area' ? {
                        color: '#3b82f6',
                        fillColor: '#3b82f6',
                        fillOpacity: 0.15,
                        weight: 2,
                        dashArray: '5, 10'
                      } : {
                        color: '#9ca3af',
                        fillColor: '#9ca3af',
                        fillOpacity: 0.05,
                        weight: 1,
                        dashArray: '3, 6'
                      }}
                    />
                  </>
                )}
                
                {/* Click handler to set location */}
                <LocationClickHandler onLocationSelect={handlePositionChange} />
              </MapContainer>
            </div>
          </div>
          
          {/* Map instructions + minimal coordinates */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {hasLocation 
                ? (language === 'nl' ? '💡 Sleep de pin om aan te passen' 
                  : language === 'es' ? '💡 Arrastra el pin para ajustar'
                  : '💡 Drag the pin to adjust')
                : (language === 'nl' ? '💡 Klik op de kaart om je buurt te selecteren'
                  : language === 'es' ? '💡 Haz clic en el mapa para seleccionar tu zona'
                  : '💡 Click on the map to select your area')}
            </p>
            {/* Minimal coordinates for power users */}
            {hasLocation && (
              <p className="text-[10px] text-muted-foreground/50 font-mono">
                {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
